import type { StereoAnalysis } from './types.js';

export interface StereoInput {
  left: Float32Array;
  right: Float32Array;
  sample_rate: number;
}

// ISO octave band centers used elsewhere in this package (see spectral.ts).
// We use the same 10-band layout for width_by_band so consumers can align bands.
const OCTAVE_CENTERS = [31.25, 62.5, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];

/**
 * Stereo analysis:
 *   correlation: Pearson correlation of L and R over the full signal.
 *   ms_ratio:    rms(M)/rms(S) where M=(L+R)/2 and S=(L-R)/2.
 *                For L≈R signals S→0 and the ratio grows large; we cap at a
 *                large finite value (1e6) to keep the field finite for JSON.
 *   width_by_band: per ISO octave band, Pearson correlation between the
 *                band-passed L and R. A naive Goertzel-style sliding band-pass
 *                is overkill here — we use a one-pole biquad band-pass at the
 *                center frequency (Q ≈ √2) and compute correlation on the
 *                resulting envelopes. Lower correlation in a band means more
 *                stereo width in that band.
 */
export function analyzeStereo(input: StereoInput): StereoAnalysis {
  const { left, right, sample_rate } = input;
  const n = Math.min(left.length, right.length);
  if (n === 0 || sample_rate <= 0) {
    return {
      correlation: 0,
      ms_ratio: 1,
      width_by_band: new Array<number>(OCTAVE_CENTERS.length).fill(0),
    };
  }

  const correlation = pearson(left, right, n);

  // M/S ratio.
  let mSumSq = 0;
  let sSumSq = 0;
  for (let i = 0; i < n; i++) {
    const m = 0.5 * (left[i] + right[i]);
    const s = 0.5 * (left[i] - right[i]);
    mSumSq += m * m;
    sSumSq += s * s;
  }
  const mRms = Math.sqrt(mSumSq / n);
  const sRms = Math.sqrt(sSumSq / n);
  let ms_ratio: number;
  if (sRms <= 1e-12) {
    ms_ratio = mRms <= 1e-12 ? 1 : 1e6;
  } else {
    ms_ratio = mRms / sRms;
  }

  const width_by_band = OCTAVE_CENTERS.map((cf) => {
    if (cf >= sample_rate / 2) return 0;
    const lf = bandPass(left, sample_rate, cf);
    const rf = bandPass(right, sample_rate, cf);
    return pearson(lf, rf, Math.min(lf.length, rf.length));
  });

  return { correlation, ms_ratio, width_by_band };
}

function pearson(a: ArrayLike<number>, b: ArrayLike<number>, n: number): number {
  if (n <= 1) return 0;
  let sa = 0;
  let sb = 0;
  for (let i = 0; i < n; i++) {
    sa += a[i];
    sb += b[i];
  }
  const ma = sa / n;
  const mb = sb / n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i] - ma;
    const xb = b[i] - mb;
    num += xa * xb;
    da += xa * xa;
    db += xb * xb;
  }
  const denom = Math.sqrt(da * db);
  if (denom <= 1e-20) return 0;
  return num / denom;
}

/**
 * Single-pole RBJ band-pass biquad at center freq `cf` with Q = √2.
 * Returns a new Float64-precision Float32Array of the filtered signal.
 */
function bandPass(x: Float32Array, sr: number, cf: number): Float32Array {
  const Q = Math.SQRT2;
  const w0 = (2 * Math.PI * cf) / sr;
  const cosw = Math.cos(w0);
  const sinw = Math.sin(w0);
  const alpha = sinw / (2 * Q);

  // RBJ band-pass (constant 0 dB peak gain): b0=alpha, b1=0, b2=-alpha,
  //                                          a0=1+alpha, a1=-2cosω, a2=1-alpha.
  const b0 = alpha;
  const b1 = 0;
  const b2 = -alpha;
  const a0 = 1 + alpha;
  const a1 = -2 * cosw;
  const a2 = 1 - alpha;

  const nb0 = b0 / a0;
  const nb1 = b1 / a0;
  const nb2 = b2 / a0;
  const na1 = a1 / a0;
  const na2 = a2 / a0;

  const out = new Float32Array(x.length);
  let xn1 = 0;
  let xn2 = 0;
  let yn1 = 0;
  let yn2 = 0;
  for (let i = 0; i < x.length; i++) {
    const xn = x[i];
    const yn = nb0 * xn + nb1 * xn1 + nb2 * xn2 - na1 * yn1 - na2 * yn2;
    out[i] = yn;
    xn2 = xn1;
    xn1 = xn;
    yn2 = yn1;
    yn1 = yn;
  }
  return out;
}
